<template>
  <div>
    <page-header
      :actions="pageHeaderActionsConfig"
      left-side-class="col-xl-4 pb-2"
      :page="headerConfig"
      right-side-class="col-xl-8 align-items-end d-flex pb-4 justify-content-end"
    />
    <add
      v-if="addOpen"
      class="mb-3"
      :collections="dataToCreation"
      :copy-from="copyFrom"
      @on-cancel="
        () => {
          this.copyFrom = null;
          this.addOpen = false;
          this.isAdding = false;
        }
      "
    />
    <filter-manager
      ref="filterRef"
      v-model="filter.model"
      class="mb-4"
      :display-advanced="false"
      :filters="filtersConfig"
      @search="onSearchHandler()"
    />

    <custom-separator class="my-4" text="Resultado" />
    <custom-result-not-found
      v-if="results.length == 0"
      text="No cuenta con información disponible."
    />
    <div v-for="row of results" :key="row.id">
      <result-row
        :data="row"
        @duplicate="duplicate"
        @reload="onSearchHandler"
        @remove="remove"
        @returnToDraft="rollbackToDraft"
      />
    </div>
    <pagination
      v-if="showPagination"
      :data="pagination"
      show-text
      @pagination-go-page="goToPageHandler"
    />
  </div>
</template>

<script>
//components
import FilterManager from 'FilterManager';
import PageHeader from 'PageHeader';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';
import storeMixin from '@/shared/mixins/storeGenerator';
import alertMixin from '@/mixins/alert.mixin.js';
import Add from './components/Add.vue';
import ResultRow from './components/ResultRow.vue';

//services
import { getInitFilters } from '@/modules/purchaseService/api.js';
import { getServiceTypesByArea } from '@/modules/serviceProposalHistory/api';
import { get, changeStatus, remove } from '@/modules/bidQuestionConfig/api.js';
import { announcementQuestionnaireStatuses } from '@/utils/constants/announcementQuestionnaireStatus';

//constants
const DEFAULT_PAGE_SIZE = 10;

export default {
  name: 'QuestionConfigMain',
  components: {
    FilterManager,
    PageHeader,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
    Add,
    ResultRow,
  },
  mixins: [storeMixin, alertMixin],
  data() {
    return {
      isAdding: false,
      addOpen: false,
      copyFrom: null,
      dataToCreation: {
        areas: [],
        contractTypes: [],
      },
      headerConfig: {
        title: 'Configuración Preguntas para Pliegos',
        icon: 'fas fa-file-invoice',
        routes: [
          'Proceso de Competencias',
          'Configuración Preguntas para Pliegos',
        ],
      },
      filter: {
        model: {
          area: null,
          service: null,
          searchText: null,
        },
        options: {
          areas: [],
          services: [],
        },
      },
      pagination: {
        currentPage: 1,
        totalItemCount: 1,
        pageCount: 1,
        count: 1,
        pageSize: DEFAULT_PAGE_SIZE,
      },
      results: [],
    };
  },
  computed: {
    showPagination() {
      return this.pagination.totalItemCount > this.pagination.pageSize;
    },
    pageHeaderActionsConfig() {
      return [
        {
          text: 'Regresar',
          visible: !this.isAdding,
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => {
            this.$router.push({
              name: 'infraestructure',
            });
          },
        },
        {
          text: 'Generar',
          visible: !this.addOpen,
          icon: 'fas fa-sync-alt',
          variant: 'success',
          click: () => {
            this.copyFrom = null;
            this.addOpen = true;
            this.isAdding = true;
          },
        },
      ];
    },
    filtersConfig() {
      return [
        {
          name: 'area',
          component: 'CustomDropDown',
          class: 'col-md-4',
          options: this.filter.options.areas,
          initialValue: this.filter.model.area,
          placeholder: 'Área',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          onChanged: this.onAreaChange,
          clearModelsOnUpdate: ['service'],
          disabled: this.filter.options.areas?.length === 1,
        },
        {
          name: 'service',
          component: 'CustomDropDown',
          class: 'col-md-4',
          options: this.filter.options.services,
          initialValue: this.filter.model.service,
          placeholder: 'Servicio a Ofrecer',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          disabled: this.filter.options.services?.length === 1,
        },
        {
          name: 'searchText',
          component: 'CustomInput',
          class: 'col-md-4',
          placeholder: 'Escriba Número de Cuestionario o Nombre',
          clearable: true,
          icon: 'fas fa-search color-primary-2',
          leftIcon: true,
        },
      ];
    },
  },
  destroyed() {
    this.persistData();
  },
  beforeRouteEnter(_, from, next) {
    const persistRoutes = [
      'questionConfigurationDetail',
      'questionConfigurationPreview',
    ];
    next(async (vm) => {
      if (persistRoutes.some((x) => x == from.name)) {
        vm.shouldRecovery = false;
        vm.onSearchHandler();
      } else {
        vm.shouldRecovery = true;
        vm.recoveryData();
        await vm.fetchInitFilters();
      }
    });
  },
  methods: {
    async fetchInitFilters() {
      const { data } = await getInitFilters(false);

      this.filter.options = {
        ...this.filter.options,
        ...data,
        services: [],
      };
      if (this.filter.options.areas?.length === 1) {
        this.filter.model.area = this.filter.options.areas[0];
        await this.onAreaChange(this.filter.model.area);
      }

      this.dataToCreation.areas = data.areas;
      this.dataToCreation.contractTypes = data.contractTypes;
    },
    async onAreaChange(area) {
      this.filter.options.services = [];
      this.filter.model.service = null;
      if (!area) return;

      const { data } = await getServiceTypesByArea(area.id);
      this.filter.options.services = data;
      if (data?.length === 1) {
        this.filter.model.service = data[0];
      }
    },
    async onSearchHandler(page = 1) {
      this.pagination.currentPage = page;

      const payload = {
        searchText: this.filter.model.searchText,
        areaId: this.filter.model.area?.id,
        serviceId: this.filter.model.service?.id,
        pageIndex: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
      };
      const { data: results = { data: [] } } = await get(payload);
      this.results = results.data.map((x) => ({ ...x, opened: false }));

      this.pagination = {
        ...this.pagination,
        totalItemCount: results.totalItemCount,
        pageCount: results.pageCount,
        currentPage: results.currentPage,
        count: results.count,
      };
    },
    async goToPageHandler(page) {
      this.pagination.currentPage = page;
      await this.onSearchHandler(page);
    },
    async rollbackToDraft(id) {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        '¿Está seguro que desea regresar la configuración a borrador?',
        'Sí',
        'No'
      );

      if (!confirmed) return;

      try {
        await changeStatus(id, announcementQuestionnaireStatuses.Draft);
        this.ShowToast(
          'Proceso exitoso',
          'Los cambios fueron guardados exitosamente',
          'success'
        );

        this.onSearchHandler();
      } catch (error) {
        this.ShowToast('Error', error.response.data.message, 'error');
      }
    },
    async remove(id) {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        '¿Está seguro que desea eliminar la configuración?',
        'Sí',
        'No'
      );

      if (!confirmed) return;

      try {
        await remove(id);
        this.ShowToast(
          'Proceso exitoso',
          'El registro fue eliminado exitosamente',
          'success'
        );

        this.onSearchHandler();
      } catch (error) {
        this.ShowToast('Error', error.response.data.message, 'error');
      }
    },
    async duplicate(data) {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        '¿Está seguro que desea copiar la configuración de este cuestionario?',
        'Sí',
        'No'
      );

      if (!confirmed) return;

      this.copyFrom = data;
      this.addOpen = true;
      this.isAdding = true;
    },
  },
};
</script>
<style scoped>
::v-deep .orange-icon {
  font-size: 18pt;
}
</style>
